import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSuppliers,
  fetchSupplierPerformance,
  InventoryApiError,
} from '../../services/inventoryApi';
import './SupplierScorecard.css';

interface SupplierScorecardProps {
  enterpriseId: string;
  selectedStore?: string;
  initialSupplierId?: string;
}

interface SupplierRow {
  supplierId: string;
  name: string;
}

interface PerformanceWeek {
  weekStart: string;
  onTimeRate: number;
  fillRate: number;
  qualityScore: number;
  substitutionRate: number;
  pricePositionPct: number;
  compositeScore?: number;
}

interface PerformanceResponse {
  supplierId: string;
  supplierName?: string;
  compositeScore?: number;
  weights?: {
    onTime: number;
    fill: number;
    quality: number;
    substitution: number;
    price: number;
  };
  current?: {
    onTime: number;
    fill: number;
    quality: number;
    substitution: number;
    price: number;
  };
  history?: PerformanceWeek[];
}

const DEFAULT_WEIGHTS = {
  onTime: 0.3,
  fill: 0.3,
  quality: 0.2,
  substitution: 0.1,
  price: 0.1,
};

const Bar: React.FC<{ value: number; weight: number; label: string }> = ({
  value,
  weight,
  label,
}) => {
  const pct = Math.max(0, Math.min(100, value * 100));
  return (
    <div className="scorecard__bar-row">
      <div className="scorecard__bar-label">
        <span>{label}</span>
        <span className="scorecard__bar-weight">
          weight {Math.round(weight * 100)}%
        </span>
      </div>
      <div className="scorecard__bar-track">
        <div className="scorecard__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="scorecard__bar-value">{pct.toFixed(1)}</div>
    </div>
  );
};

const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
  if (!values.length) return <span className="scorecard__spark-empty">No data</span>;
  const w = 120;
  const h = 28;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / Math.max(1, values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className="scorecard__spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline
        fill="none"
        stroke="#2563eb"
        strokeWidth={1.5}
        points={points}
      />
    </svg>
  );
};

const SupplierScorecard: React.FC<SupplierScorecardProps> = ({
  enterpriseId,
  initialSupplierId,
}) => {
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [supplierId, setSupplierId] = useState<string>(initialSupplierId || '');
  const [perf, setPerf] = useState<PerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSuppliers = async () => {
      if (!enterpriseId) return;
      try {
        const res = await fetchSuppliers(enterpriseId);
        const list: SupplierRow[] = res?.suppliers || res?.data || res || [];
        setSuppliers(list);
        if (!supplierId && list.length > 0) {
          setSupplierId(list[0].supplierId);
        }
      } catch (e) {
        // non-fatal, user can still enter an ID manually
      }
    };
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterpriseId]);

  const loadPerformance = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSupplierPerformance(supplierId);
      setPerf(res);
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to load performance');
      setPerf(null);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    loadPerformance();
  }, [loadPerformance]);

  const weights = perf?.weights || DEFAULT_WEIGHTS;
  const current = perf?.current || {
    onTime: 0,
    fill: 0,
    quality: 0,
    substitution: 0,
    price: 0,
  };

  const composite = useMemo(() => {
    if (perf?.compositeScore != null) return perf.compositeScore;
    return (
      current.onTime * weights.onTime +
      current.fill * weights.fill +
      current.quality * weights.quality +
      (1 - current.substitution) * weights.substitution +
      current.price * weights.price
    );
  }, [perf, current, weights]);

  const history = perf?.history || [];

  return (
    <div className="scorecard">
      <div className="scorecard__header">
        <div>
          <h2>Supplier Scorecard</h2>
          <p className="scorecard__sub">
            Composite performance with weighted sub-scores
          </p>
        </div>
        <div className="scorecard__selector">
          <label>Supplier</label>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">Select supplier...</option>
            {suppliers.map((s) => (
              <option key={s.supplierId} value={s.supplierId}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className="scorecard__state">Loading...</div>}
      {error && <div className="scorecard__error">{error}</div>}

      {!loading && !error && perf && (
        <>
          <div className="scorecard__composite">
            <div className="scorecard__composite-label">Composite Score</div>
            <div className="scorecard__composite-value">
              {(composite * 100).toFixed(1)}
              <span>/100</span>
            </div>
            <div className="scorecard__composite-sub">
              {perf.supplierName || supplierId}
            </div>
          </div>

          <div className="scorecard__bars">
            <Bar label="On-Time Delivery" value={current.onTime} weight={weights.onTime} />
            <Bar label="Fill Rate" value={current.fill} weight={weights.fill} />
            <Bar label="Quality" value={current.quality} weight={weights.quality} />
            <Bar
              label="Substitution (inverted)"
              value={1 - current.substitution}
              weight={weights.substitution}
            />
            <Bar label="Price Competitiveness" value={current.price} weight={weights.price} />
          </div>

          <div className="scorecard__history">
            <h3>Last 12 Weeks</h3>
            {history.length === 0 ? (
              <div className="scorecard__state">No historical data yet.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>On-Time</th>
                    <th>Fill</th>
                    <th>Quality</th>
                    <th>Sub</th>
                    <th>Price</th>
                    <th>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(-12).map((w) => (
                    <tr key={w.weekStart}>
                      <td>{w.weekStart}</td>
                      <td>{(w.onTimeRate * 100).toFixed(1)}%</td>
                      <td>{(w.fillRate * 100).toFixed(1)}%</td>
                      <td>{(w.qualityScore * 100).toFixed(1)}%</td>
                      <td>{(w.substitutionRate * 100).toFixed(1)}%</td>
                      <td>{(w.pricePositionPct * 100).toFixed(1)}%</td>
                      <td>
                        <Sparkline
                          values={history.map((h) => h.compositeScore ?? 0)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!loading && !error && !perf && supplierId && (
        <div className="scorecard__state">No performance data yet for this supplier.</div>
      )}
      {!supplierId && (
        <div className="scorecard__state">Select a supplier to view the scorecard.</div>
      )}
    </div>
  );
};

export default SupplierScorecard;
